// ══════════════════════════════════════════════════════════════════
//  낮이 **소유하는** 값 — 밤의 config.js 자리.
//
//  ■ 여기 든 것은 **낮 규칙이 직접 소유하는 값**뿐이다:
//      ① 3축 비용 사다리   ② 별자리 게이트 밤   ③ 안식 사료비율   ④ 재시도 식량비
//      ⑤ 장별 우유 단가(상인 양 가격의 기준)
//    나머지(환율·손실 정산표·손실 마리 수·스폰)는 **밤이나 산출기가
//    이미 갖고 있다.** 낮이 복사해 들면 언젠가 두 값이 갈린다 —
//    이 프로젝트가 실제로 그 사고를 여러 번 냈다.
//
//  ■ 그래서 **장(章) 판정은 밤 config 에서 import 한다.** 낮이 다시 적지 않는다.
//
//  ■ 장별 우유 단가 5/6/8/10/12/14와 새 양 가격 단가×5는 시스템명세 v1.2의
//    확정값이다. 그 밖의 경제 수치는 여전히 마지막 재수렴 전까지 확정하지 않는다.
// ══════════════════════════════════════════════════════════════════
import { chapterOf, CAMPAIGN_DAYS, SABBATH_NIGHTS as CFG_SABBATH_NIGHTS,
         nightKeyOfCard } from '../gb/config.js';

export { chapterOf };

// ── 장별 우유 단가 · 상인 가격 (시스템명세 5장, v1.2 확정) ───────
// 0번 칸은 쓰지 않는다. `chapterOf()`의 1~6을 그대로 인덱스로 삼아 경계가
// 밤 규칙과 갈리지 않게 한다.
export const MILK_UNIT_PRICE_BY_CHAPTER = Object.freeze([null, 5, 6, 8, 10, 12, 14]);
export const SHEEP_PRICE_MULTIPLIER = 5;

// ── 여정을 시작할 때 손에 든 치즈 ────────────────────────────────
// 【사장님 지시 2026-08-01】 *"우선 첫날에 10치즈 들고 시작하자."*
//   구 값은 **0** 이었다 — 첫 낮에 살 수 있는 것이 하나도 없었다는 뜻이다
//   (제일 싼 3축 1단계가 50치즈, 상인 물건은 그 위다).
//   ⚠ **첫 낮에 무엇을 살 수 있게 되는지는 아직 안 정해졌다.** 10 은 3축 1단계(50)에도
//     못 미치므로 지금은 「빈손이 아니다」는 것 말고는 바뀌는 것이 없다. 무엇을 살 수
//     있어야 하는지는 사장님 결정이고, 여기서 지어내지 않는다.
//   ⚠ 치즈는 낮↔밤 계속 이월되므로(사장님 지시 2026-07-29) 이 값은 **첫 밤 지갑에도**
//     그대로 실린다(`nightio.toNightOpts` → `walletCarry`).
export const START_CHEESE = 10;

export function milkUnitPriceForNight(nightNo) {
  return MILK_UNIT_PRICE_BY_CHAPTER[chapterOf(nightNo)];
}

export function sheepPriceForNight(nightNo) {
  return milkUnitPriceForNight(nightNo) * SHEEP_PRICE_MULTIPLIER;
}

// ⚠⚠ **3축 비용 사다리(`AXIS_COST`)와 별자리 게이트(`GATE_NIGHT`)가 여기 있었다**
//   【폐기 2026-08-07 — 사장님: *"별자리 시스템 폐기라니까. 처음부터 돈만 있으면
//   다 살수 있어"* · *"일감자리 없애고 상인에 다 편입시켜"*】.
//     · `AXIS_COST = [50,100,170,270,380,470]` — 축당 1,440 · 3축 4,320(불변식 ④)
//     · `GATE_NIGHT` — 「치즈로 못 사는 시간의 문」. 40일에 맞춰 [0,0,7,13,20,27] 이었다.
//   둘 다 축이 있어야 뜻이 있는 값이라 축과 같이 걷었다. **관문은 이제 가격 하나**이고,
//   그 곡선은 `goods.js` 의 「값」 절이 든다. 불변식 ④는 좌변이 없어져 폐기됐다.

// ══════════════════════════════════════════════════════════════════
//  ── ③ 안식일 ── **모델이 바뀌었다**  【사장님 지시 2026-08-03】
//
//  ■ 구 모델: 밤 7·14·21·28 **시작 전에 끼워 넣는 여분의 낮.** 여정 칸을 안 썼고
//    그래서 안식일은 「번호 없는 날」이었다. 넷이었다.
//  ■ 새 모델: **안식일은 40일 안에 번호가 있는 날이다.** 7·14·21·28·35 — 다섯 번.
//    그 날은 낮도 밤도 안식일이고, 밤은 **그 날 낮 디오라마에 어둠만 씌운 화면**이다.
//    상인도 안 오고 젖도 안 짠다. *"말 그대로 진짜 정통 안식일. 아무것도 안 하고 쉬는날."*
//
//  ⚠ **표는 `gb/config.js` 하나다.** 여기서 `[7,14,21,28,35]` 를 손으로 다시 적으면
//    밤 표와 낮 표가 갈리고, 그러면 **낮은 안식일이라는데 밤은 전투**가 된다.
//    이 저장소가 늘 깨지던 자리다(`fieldart.js`·`gearart.js` 주석 참조).
// ══════════════════════════════════════════════════════════════════
export const SABBATH_NIGHTS = CFG_SABBATH_NIGHTS.map((k) => Number(k.slice(1)));
// 사료비 = 무리 수 × (그 장 우유 단가 × REST_FEED_RATE).
// 한 자리에 하루 더 있으면 그 자리의 풀이 마른다 — 디제시스 정합.
export const REST_FEED_RATE = 0.5;
// **바닥에서는 면제한다** — 멈추는 것이 벌이 되지 않게.
export const REST_FEED_EXEMPT_FLOCK = 25;
// 핵심 양 상한. 안식 4회분의 승격을 받기 위해 8~12 → 8~16 으로 확대됐다.
export const CORE_MAX = 16;

// ⚠ **`RETRY_FOOD_COST`(재시도 낮 식량비 20)가 여기 있었다** 【40일 개편 2026-08-03】.
//   재시도가 삭제되며 부를 곳이 사라졌다 — 사장님 지시가 *"실패하면 양을 잃지.
//   **그것 뿐이야.**"* 였으므로 되살릴 자리도 없다.

// ── ⑤ 식탁 — N24 낮의 나눔 (30밤시트 N24 · 서사바이블 5장) ───────
// *"**피난민**(나누는 사람). 치즈를 **덩이 단위**로 나눌 수 있다
//  (**1덩이 = 50치즈** 내부 환산, 나눔 = 3덩이 ~150치즈)"*
// N24 낮에 온다. 상점 흐름을 재사용해 치즈를 나눈다.
// 시편 23:5 의 그림을 밤 로스터가 아니라 **거래 화면**이 배달한다(본문 인용 없음).
// ⚠ **날짜를 손으로 적지 않는다** 【40일 개편】. 식탁은 구 카드 N24(「식탁의 낮과
//   폐허의 밤」)에 붙은 사건이고, 그 카드는 40일에서 **31일**로 갔다. 24 를 그대로
//   뒀으면 엉뚱한 낮에 식탁이 차려졌을 것이다.
export const TABLE_NIGHT = Number((nightKeyOfCard('N24') ?? 'N31').slice(1));
export const CHUNK = 50;              // 1덩이. 연출 단위 ↔ 경제 단위의 환산(명세 9장)
export const SHARE_CHUNKS = 3;        // 나눔 = 3덩이
export const SHARE_COST = CHUNK * SHARE_CHUNKS;   // 150
// **보상은 계수 하나다** — *"그 밤 안정성 감소 −20% 완화"*. 구 둘째 채널
// 「진입 방향 사전 경고」는 폐기됐다(개 짖음이 연출로 강등돼 실어 보낼 정보가 없다).
export const SHARE_STAB_DROP_MUL = 0.8;
// ⚠ **금액 계상은 재스냅샷 대기**다(30밤시트 N24) — 나눔 150은 원장 `misc` 에 없다.
//   나눈 회차는 그만큼 3축이 늦어진다. **의도된 트레이드오프**이지 버그가 아니다.

// ── 정적 밤 · 무리 ───────────────────────────────────────────────
// ⚠ **정적 밤(`STATIC_NIGHTS`·`isStaticNight`)이 여기 있었다** 【40일 개편 2026-08-03】.
//   구 N16(소생)·N21(가장 평화로운)은 *"전투 없음 — 안정성 100% 확정"* 이라
//   **안식일과 같은 물건**이었다. 둘을 남겨 두면 「쉬는 날」이 두 종류가 되어 규칙이
//   흐려진다 — 안식일이 흡수했고(config 의 카드 표), 부르는 자리도 전부 걷었다.
// 여정의 길이. **마지막 밤을 깨면 밤 번호가 그 다음이 되고, 그것은 밤이 아니라 끝이다.**
// ⚠ 30 이 손으로 박혀 있었다 — 40일 개편에서 `config.CAMPAIGN_DAYS` 하나로 모았다.
//   표가 둘이면 낮은 30일에 끝났다고 하고 밤 표에는 40일이 있는 상태가 된다.
export const NIGHT_COUNT = CAMPAIGN_DAYS;
// 낮 디오라마와 밤 인계가 함께 읽는 시작 무리 수. 핵심 양 3마리를 포함한 총수다.
export const FLOCK_START = 30;
// ⚠ 외상 부채 3상수(트리거 25 · 원금 상한 600 · 공제 20%)는 **삭제됐다**
//   (사장님 지시 2026-07-29 — 「부채 개념 없애. 그냥 현재 잔액만」).
//   정본 명세에는 아직 남아 있다 — 문서 정리 때 같이 뺀다. ledger.js 주석 참조.

// ── 파생 ─────────────────────────────────────────────────────────
// ⚠ `tierOf(nightNo)`(별자리 단계)가 여기 있었다 — 게이트 폐기(위 주석).
export const isSabbathNight = (n) => SABBATH_NIGHTS.includes(n);

// ── 여기 없는 것 — 일부러 비운 자리 ──────────────────────────────
//  · 손실 정산표           → **밤이 settle() 로 낸 것을 그대로 쓴다.** 낮이 배율표를
//                            들면 두 곳이 갈린다(코드 86/78/70/40 vs 명세가 다르다)
//  · 조각→치즈 환율        → 밤 config.PIECE.perCheese          [경제는 마지막에]
//  · 상인 재고 배치         → 무리 유입표가 밤별 방문 재고를 소유한다
//  · 다운 밤 가산 손실량    → 명세 4-1 『가산량은 측정 대기』      [측정 대기]
//  · 개 배치 「방침」 목록   → 디자인 2-A 가 창구만 열고 **선택지를 안 적었다** [정본 미기재]
